import React, { Children, isValidElement, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MarkdownCodeBlock } from '@/frontend/components/MarkdownCodeBlock';
import { InteractiveMarkdownTable } from '@/frontend/components/InteractiveMarkdownTable';
import { normalizeImplicitCodeBlocks } from '@/frontend/components/implicitCodeBlocks';

interface SimpleMarkdownProps {
  content: string;
  className?: string;
  allowImages?: boolean;
  allowVideoEmbeds?: boolean;
  interactiveTables?: boolean;
  detectCodeBlocks?: boolean;
  allowLinkHref?: (href: string) => boolean;
  renderCodeBlock?: (code: string, language: string | undefined) => React.ReactNode;
}

function videoEmbedUrl(href: string | undefined): string | null {
  if (!href) return null;
  try {
    const url = new URL(href);
    let id: string | null = null;
    if (['youtube.com', 'www.youtube.com'].includes(url.hostname)) {
      id = url.searchParams.get('v') || url.pathname.match(/^\/(?:shorts|embed)\/([\w-]+)/)?.[1] || null;
      return id && /^[\w-]+$/.test(id) ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }
    if (url.hostname === 'youtu.be') {
      id = url.pathname.slice(1);
      return /^[\w-]+$/.test(id) ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }
    if (['vimeo.com', 'www.vimeo.com'].includes(url.hostname)) {
      id = url.pathname.match(/^\/(\d+)/)?.[1] || null;
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
  } catch {
    return null;
  }
  return null;
}

export const SimpleMarkdown: React.FC<SimpleMarkdownProps> = ({
  content,
  className = '',
  allowImages = true,
  allowVideoEmbeds = false,
  interactiveTables = false,
  detectCodeBlocks = false,
  allowLinkHref,
  renderCodeBlock,
}) => {
  const renderedContent = useMemo(
    () => detectCodeBlocks ? normalizeImplicitCodeBlocks(content) : content,
    [content, detectCodeBlocks],
  );

  return (
    <div className={`prose prose-sm max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
        // Headings
        h1: ({ children }) => <h1 className="text-xl font-bold text-gray-900 dark:text-foreground mb-3 mt-4 first:mt-0">{children}</h1>,
        h2: ({ children }) => <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground mb-2 mt-3 first:mt-0">{children}</h2>,
        h3: ({ children }) => <h3 className="text-base font-semibold text-gray-900 dark:text-foreground mb-2 mt-3 first:mt-0">{children}</h3>,
        h4: ({ children }) => <h4 className="text-sm font-semibold text-gray-900 dark:text-foreground mb-1 mt-2 first:mt-0">{children}</h4>,
        
        // Paragraphs
        p: ({ children }) => <p className="text-gray-900 dark:text-foreground mb-3 leading-relaxed last:mb-0">{children}</p>,
        
        // Lists
        ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1 text-gray-900 dark:text-foreground">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-gray-900 dark:text-foreground">{children}</ol>,
        li: ({ children }) => <li className="text-gray-900 dark:text-foreground">{children}</li>,
        
        // ReactMarkdown wraps fenced code in `pre`. Owning that wrapper avoids
        // nested <pre> markup and gives language-less fences the same controls.
        pre: ({ children }) => {
          const child = Children.count(children) === 1 ? Children.only(children) : null;
          if (!isValidElement<{ className?: string; children?: React.ReactNode }>(child)) {
            return <pre>{children}</pre>;
          }

          const language = /language-([\w-]+)/.exec(child.props.className || '')?.[1];
          const code = String(child.props.children ?? '').replace(/\n$/, '');
          const rendered = renderCodeBlock?.(code, language);
          if (rendered != null) return <>{rendered}</>;
          return <MarkdownCodeBlock code={code} language={language} />;
        },
        code: ({ children, ...props }) => (
          <code
            className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em] text-foreground"
            {...props}
          >
            {children}
          </code>
        ),
        
        // Tables
        table: ({ children }) => interactiveTables ? (
          <InteractiveMarkdownTable>{children}</InteractiveMarkdownTable>
        ) : (
          <div className="mb-3 overflow-x-auto rounded-lg border border-border">
            <table className="min-w-full border-collapse [&_thead]:bg-muted [&_th]:border-b [&_th]:border-r [&_th]:border-border [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:text-sm [&_th]:font-semibold [&_td]:border-r [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_td]:text-sm [&_tr]:border-b [&_tr]:border-border [&_tr:last-child]:border-b-0 [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0">
              {children}
            </table>
          </div>
        ),

        // Links
        a: ({ children, href }) => {
          if (href && allowLinkHref && !allowLinkHref(href)) return <span>{children}</span>;
          const embedUrl = allowVideoEmbeds && String(children) === href ? videoEmbedUrl(href) : null;
          return embedUrl ? (
            <span className="my-5 block aspect-video overflow-hidden rounded-xl border border-border bg-black shadow-sm">
              <iframe src={embedUrl} title="Vídeo incorporado" className="h-full w-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen />
            </span>
          ) : (
            <a href={href} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          );
        },

        img: ({ src, alt }) => {
          if (!allowImages) {
            if (src && allowLinkHref && !allowLinkHref(src)) return <span>{alt || src}</span>;
            return src ? (
              <a
                href={src}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {alt || src}
              </a>
            ) : null;
          }

          return <img src={src} alt={alt || ''} className="rounded-md" />;
        },
        
        // Blockquotes
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-gray-300 dark:border-border pl-4 mb-3 text-gray-600 dark:text-muted-foreground italic">
            {children}
          </blockquote>
        ),
        
        // Horizontal rules
        hr: () => <hr className="border-gray-300 dark:border-border my-4" />,
        
        // Strikethrough (GFM)
        del: ({ children }) => <del className="line-through text-gray-500 dark:text-muted-foreground">{children}</del>,
        
        // Task lists (GFM)
        input: ({ type, checked, ...props }) => {
          if (type === 'checkbox') {
            return (
              <input
                type="checkbox"
                checked={checked}
                disabled
                className="mr-2 accent-blue-600"
                {...props}
              />
            );
          }
          return <input type={type} {...props} />;
        },
      }}
    >
      {renderedContent}
    </ReactMarkdown>
    </div>
  );
};
